import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Checkbox,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  LockOutlined as LockOutlinedIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import VistaFactura from '../facturapos/VistaFactura';

const PROGRAMA_REGISTRO_PAGOS = 'registropagosclientescredito';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatearFecha = (fecha) => {
  if (!fecha) return '—';
  try {
    return new Date(fecha).toLocaleDateString('es-DO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return fecha;
  }
};

const rowKey = (f) => `${f.tipofactura}|||${f.nfact}`;

const nombreClienteCxC = (f) => (f?.nombre_cliente || f?.suplidor || '').trim() || '—';

/**
 * Tope cobrable: total + N/D − pagado − N/C
 * (igual que `listarcuentasxcobrar` en el backend).
 */
const topeMontoPagoCxC = (f) => {
  const total = parseFloat(f?.total) || 0;
  const nd = parseFloat(f?.total_notas_debito) || 0;
  const vp = parseFloat(f?.valorpagado) || 0;
  const nc = parseFloat(f?.total_notas_credito) || 0;
  return Math.round((total + nd - vp - nc) * 100) / 100;
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

/**
 * Registro de pagos de clientes a crédito en lote: lista facturas con saldo
 * pendiente, permite seleccionarlas, indicar montos y aplicar el cobro con una
 * sola forma de pago y fecha. Seguridad: programa `registropagosclientescredito`.
 */
const RegistroPagosClientesCredito = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_REGISTRO_PAGOS),
    [roleDetalles]
  );
  const puedeModificar = useMemo(
    () => puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_REGISTRO_PAGOS),
    [roleDetalles]
  );

  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [formasPagoDb, setFormasPagoDb] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [cargandoFormas, setCargandoFormas] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [filtroCliente, setFiltroCliente] = useState('');
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [montos, setMontos] = useState({});

  const [formaPago, setFormaPago] = useState('');
  const [referencia, setReferencia] = useState('');
  const [fechaPago, setFechaPago] = useState(() => new Date().toISOString().slice(0, 10));

  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [datosVistaFactura, setDatosVistaFactura] = useState({});

  const cargarFacturasPendientes = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    setCargandoLista(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_listarcuentasxcobrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setFacturasPendientes(data.facturas || []);
        setSeleccionados(new Set());
        setMontos({});
      } else {
        showError(data.Error || 'Error al cargar facturas pendientes');
        setFacturasPendientes([]);
      }
    } catch {
      showError('Error de conexión al cargar facturas');
      setFacturasPendientes([]);
    } finally {
      setCargandoLista(false);
    }
  }, [puedeLeer, selectedCompany, usuarioLog, claveLog, servidor, showError]);

  const cargarFormasPago = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) {
      setFormasPagoDb([]);
      return;
    }
    setCargandoFormas(true);
    try {
      const response = await fetch(servidor + '/api/configuracionbackend_listarformasdepago', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setFormasPagoDb(data.formas_pago || []);
      } else {
        setFormasPagoDb([]);
        if (data.Error) showError(data.Error);
      }
    } catch {
      setFormasPagoDb([]);
    } finally {
      setCargandoFormas(false);
    }
  }, [puedeLeer, selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    cargarFacturasPendientes();
    cargarFormasPago();
  }, [cargarFacturasPendientes, cargarFormasPago]);

  useEffect(() => {
    if (formasPagoDb.length === 0) {
      setFormaPago('');
      return;
    }
    setFormaPago((prev) => {
      if (prev && formasPagoDb.some((r) => (r.documento || '') === prev)) return prev;
      return formasPagoDb[0].documento || '';
    });
  }, [formasPagoDb]);

  const filaFormaPagoActual = useMemo(
    () => formasPagoDb.find((r) => (r.documento || '') === formaPago),
    [formasPagoDb, formaPago]
  );

  const requiereReferencia =
    Boolean(filaFormaPagoActual) &&
    String(filaFormaPagoActual.necesita_referencia || 'N').toUpperCase() === 'S';

  useEffect(() => {
    if (!requiereReferencia) {
      setReferencia('');
    }
  }, [requiereReferencia]);

  const filasFiltradas = useMemo(() => {
    const q = filtroCliente.trim().toLowerCase();
    if (!q) return facturasPendientes;
    return facturasPendientes.filter((f) => {
      const nombre = (f.nombre_cliente || '').toLowerCase();
      const codigo = (f.suplidor || '').toLowerCase();
      return nombre.includes(q) || codigo.includes(q);
    });
  }, [facturasPendientes, filtroCliente]);

  const filasOrdenadas = useMemo(() => {
    return [...filasFiltradas].sort((a, b) => {
      const ca = nombreClienteCxC(a).localeCompare(nombreClienteCxC(b));
      if (ca !== 0) return ca;
      return (a.fecha || '').localeCompare(b.fecha || '');
    });
  }, [filasFiltradas]);

  const toggleFila = (f) => {
    const k = rowKey(f);
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
      } else {
        next.add(k);
        setMontos((m) => ({
          ...m,
          [k]: m[k] ?? String(topeMontoPagoCxC(f).toFixed(2)),
        }));
      }
      return next;
    });
  };

  const seleccionarTodoFiltrado = () => {
    const next = new Set();
    const m = { ...montos };
    filasOrdenadas.forEach((f) => {
      const k = rowKey(f);
      next.add(k);
      if (m[k] == null || m[k] === '') {
        m[k] = String(topeMontoPagoCxC(f).toFixed(2));
      }
    });
    setMontos(m);
    setSeleccionados(next);
  };

  const limpiarSeleccion = () => {
    setSeleccionados(new Set());
  };

  const todosFiltradosMarcados =
    filasOrdenadas.length > 0 && filasOrdenadas.every((f) => seleccionados.has(rowKey(f)));

  const toggleTodosFiltrados = () => {
    if (todosFiltradosMarcados) {
      const keysFiltrados = new Set(filasOrdenadas.map((f) => rowKey(f)));
      setSeleccionados((prev) => {
        const next = new Set(prev);
        keysFiltrados.forEach((k) => next.delete(k));
        return next;
      });
    } else {
      seleccionarTodoFiltrado();
    }
  };

  const setMontoFila = (k, valor) => {
    setMontos((m) => ({ ...m, [k]: valor }));
  };

  const lineasPago = useMemo(() => {
    const out = [];
    seleccionados.forEach((k) => {
      const m = parseFloat(montos[k]) || 0;
      if (m <= 0) return;
      const f = facturasPendientes.find((x) => rowKey(x) === k);
      if (!f) return;
      const tope = topeMontoPagoCxC(f);
      if (m > tope + 0.001) return;
      out.push({ nfact: f.nfact, tipofactura: f.tipofactura, monto: m });
    });
    return out;
  }, [seleccionados, montos, facturasPendientes]);

  const totalPagar = useMemo(() => {
    let t = 0;
    seleccionados.forEach((k) => {
      const raw = montos[k];
      if (raw === undefined || raw === null || String(raw).trim() === '') return;
      const n = parseFloat(String(raw).replace(',', '.'));
      if (Number.isFinite(n) && n > 0) t += n;
    });
    return t;
  }, [seleccionados, montos]);

  const reimprimirFactura = async (nfact, tipofactura) => {
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerfactura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nfact,
          tipofactura,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const factura = data.factura || {};
        setDatosVistaFactura({
          ...factura,
          id_empresa: selectedCompany,
        });
        setVistaFacturaAbierta(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al obtener la factura');
      }
    } catch {
      showError('Error de conexión al obtener la factura');
    }
  };

  const handleGuardarLote = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para registrar pagos');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (lineasPago.length === 0) {
      showError(
        'Seleccione facturas e indique montos válidos: mayores a cero y no mayores al tope (total + N/D − pagado − N/C).'
      );
      return;
    }
    if (!formaPago || formasPagoDb.length === 0) {
      showError('Configure y seleccione una forma de pago en Configuración (Gestión de documentos / formas de pago).');
      return;
    }
    if (requiereReferencia && !(referencia || '').trim()) {
      showError('La forma de pago seleccionada exige referencia (cheque, transferencia, etc.).');
      return;
    }
    setGuardando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_aplicarpagoslote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          pagos: lineasPago,
          forma_pago: formaPago,
          banco_id: '',
          referencia: requiereReferencia ? (referencia || '').trim() : '',
          fecha_pago: fechaPago,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showSuccess(data.mensaje || 'Pagos registrados');
        setReferencia('');
        await cargarFacturasPendientes();
      } else {
        showError(data.Error || 'No se pudieron registrar los pagos');
      }
    } catch {
      showError('Error de conexión al registrar pagos');
    } finally {
      setGuardando(false);
    }
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Registro de pagos — clientes a crédito"
          subtitle={
            nombreNegocio
              ? `${nombreNegocio} · Cobros en lote (pagoscli)`
              : 'Cobros en lote (pagoscli)'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 560,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «registropagosclientescredito» en roles para registrar pagos
              de clientes a crédito.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registro de pagos — clientes a crédito"
        subtitle={
          nombreNegocio ? `${nombreNegocio} · Cobros en lote (pagoscli)` : 'Cobros en lote (pagoscli)'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.infoSoft,
          }}
        >
          Puede procesar varias facturas en un solo movimiento con la misma forma de pago y fecha. Las formas de pago salen de
          Configuración.
        </Alert>

        {selectedCompany?.db_empresa && !cargandoFormas && formasPagoDb.length === 0 && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.warningSoft,
            }}
          >
            No hay formas de pago configuradas. Créelas en{' '}
            <strong>Configuración → Gestión de Formas de Pago</strong> (documento = texto que se guardará
            en el cobro, p. ej. «Efectivo» o «Transferencia»).
          </Alert>
        )}

        {!puedeModificar && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.warningSoft,
            }}
          >
            Solo lectura: no tiene permiso para registrar pagos.
          </Alert>
        )}

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaymentIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del pago
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
              Se aplican a todas las facturas seleccionadas
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha del pago"
                  type="date"
                  value={fechaPago}
                  onChange={(e) => setFechaPago(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="forma-pago-label" shrink>
                    Forma de pago
                  </InputLabel>
                  <Select
                    labelId="forma-pago-label"
                    label="Forma de pago"
                    value={formaPago}
                    onChange={(e) => {
                      setFormaPago(e.target.value);
                      setReferencia('');
                    }}
                    disabled={cargandoFormas || formasPagoDb.length === 0}
                    displayEmpty
                  >
                    {formasPagoDb.length === 0 ? (
                      <MenuItem value="" disabled>
                        Sin opciones
                      </MenuItem>
                    ) : (
                      formasPagoDb.map((fp) => (
                        <MenuItem key={fp.id} value={fp.documento || ''}>
                          {fp.documento || '(sin nombre)'}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Typography sx={{ mt: 0.5, color: FP.color.textMuted, fontSize: '0.74rem' }}>
                  {cargandoFormas
                    ? 'Cargando…'
                    : filaFormaPagoActual
                      ? requiereReferencia
                        ? 'Debe completar referencia abajo'
                        : 'No requiere referencia'
                      : ' '}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Referencia"
                  value={requiereReferencia ? referencia : ''}
                  onChange={(e) => setReferencia(e.target.value)}
                  placeholder={requiereReferencia ? 'Nº cheque, transferencia, etc.' : ''}
                  disabled={!requiereReferencia}
                  required={requiereReferencia}
                  error={requiereReferencia && !(referencia || '').trim()}
                  helperText={
                    requiereReferencia
                      ? 'Obligatorio: según configuración de la forma de pago'
                      : 'Deshabilitado: esta forma de pago no exige referencia'
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {(lineasPago.length > 0 || seleccionados.size > 0) && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Facturas seleccionadas"
                value={String(seleccionados.size)}
                icon={PlaylistAddCheckIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Líneas válidas a procesar"
                value={String(lineasPago.length)}
                icon={PaymentIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total a pagar"
                value={`$${fmtMoney(totalPagar)}`}
                icon={AttachMoneyIcon}
                accent={FP.color.navy}
              />
            </Grid>
          </Grid>
        )}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlaylistAddCheckIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Facturas pendientes
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  cargarFacturasPendientes();
                  cargarFormasPago();
                }}
                disabled={cargandoLista}
                sx={{
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Actualizar lista
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleGuardarLote}
                disabled={
                  guardando ||
                  !puedeModificar ||
                  lineasPago.length === 0 ||
                  !formaPago ||
                  formasPagoDb.length === 0 ||
                  (requiereReferencia && !(referencia || '').trim())
                }
                sx={{
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: FP.color.surfaceAlt, color: FP.color.textMuted },
                }}
              >
                {guardando ? 'Procesando…' : `Registrar pago(s)${lineasPago.length ? ` · $${fmtMoney(totalPagar)}` : ''}`}
              </Button>
            </Box>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} sx={{ mb: 2 }} alignItems="flex-end">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Buscar por cliente"
                  value={filtroCliente}
                  onChange={(e) => setFiltroCliente(e.target.value)}
                  placeholder="Nombre del cliente en la factura"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: FP.color.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.86rem' }}>
                    Mostrando <strong style={{ color: FP.color.textPrimary }}>{filasOrdenadas.length}</strong> de{' '}
                    {facturasPendientes.length} pendiente(s)
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={seleccionarTodoFiltrado}
                    disabled={filasOrdenadas.length === 0}
                    sx={{
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Seleccionar todo (filtrado)
                  </Button>
                  <Button
                    size="small"
                    onClick={limpiarSeleccion}
                    disabled={seleccionados.size === 0}
                    sx={{
                      color: FP.color.textSecondary,
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: FP.color.surfaceAlt },
                    }}
                  >
                    Quitar selección
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {cargandoLista ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : facturasPendientes.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <PaymentIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay facturas pendientes
                </Typography>
                <Typography
                  sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}
                  textAlign="center"
                  px={2}
                >
                  No hay facturas a crédito con saldo pendiente.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: 560,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ ...cellHeadSx }}>
                        <Checkbox
                          indeterminate={
                            filasOrdenadas.some((f) => seleccionados.has(rowKey(f))) && !todosFiltradosMarcados
                          }
                          checked={todosFiltradosMarcados}
                          onChange={toggleTodosFiltrados}
                          inputProps={{ 'aria-label': 'Seleccionar todo filtrado' }}
                          sx={{
                            color: FP.color.textMuted,
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: FP.color.navy },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cellHeadSx}>Nº</TableCell>
                      <TableCell sx={cellHeadSx}>Nombre cliente</TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                        Fecha
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                        NCF
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                        Saldo
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                        Pagado
                      </TableCell>
                      <TableCell
                        sx={{ ...cellHeadSx, textAlign: 'right', minWidth: 140 }}
                        align="right"
                      >
                        Monto a pagar
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                        N/C
                      </TableCell>
                      <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                        Reimprimir
                      </TableCell>
                    </TableRow>
                    {filasOrdenadas.map((f) => {
                      const k = rowKey(f);
                      const sel = seleccionados.has(k);
                      const tope = topeMontoPagoCxC(f);
                      const valM = montos[k] ?? '';
                      const mNum = parseFloat(valM) || 0;
                      const errMonto = sel && valM !== '' && (mNum <= 0 || mNum > tope + 0.001);
                      return (
                        <TableRow
                          key={k}
                          hover
                          selected={sel}
                          sx={{
                            backgroundColor: sel ? FP.color.accentBlueSoft : 'transparent',
                            borderLeft: sel ? `4px solid ${FP.color.accentBlue}` : '4px solid transparent',
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={sel}
                              onChange={() => toggleFila(f)}
                              sx={{
                                color: FP.color.textMuted,
                                '&.Mui-checked': { color: FP.color.navy },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            <Typography
                              sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.86rem' }}
                            >
                              {f.nfact}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: FP.color.textMuted,
                                fontSize: '0.72rem',
                                maxWidth: 160,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {f.tipofactura}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.86rem' }}
                            >
                              {nombreClienteCxC(f)}
                            </Typography>
                            {f.nombre_cliente &&
                              String(f.suplidor || '').trim() !== String(f.nombre_cliente || '').trim() && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: FP.color.textMuted, display: 'block', fontSize: '0.72rem' }}
                                >
                                  Ref. {f.suplidor}
                                </Typography>
                              )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontSize: '0.85rem', color: FP.color.textPrimary }}
                          >
                            {formatearFecha(f.fecha)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontSize: '0.82rem', color: FP.color.textSecondary }}
                          >
                            {f.ncf || '—'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              fontWeight: 700,
                              color: FP.color.textPrimary,
                              fontSize: '0.88rem',
                            }}
                          >
                            ${fmtMoney(f.saldo_pendiente)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              fontSize: '0.86rem',
                              color: FP.color.textSecondary,
                            }}
                          >
                            ${fmtMoney(f.valorpagado)}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={valM}
                              onChange={(e) => setMontoFila(k, e.target.value)}
                              disabled={!sel || !puedeModificar}
                              error={errMonto}
                              helperText={
                                errMonto
                                  ? `Máx. ${fmtMoney(tope)} (total + N/D − pagado − N/C)`
                                  : undefined
                              }
                              FormHelperTextProps={{
                                sx: {
                                  mt: 0.25,
                                  textAlign: 'right',
                                  whiteSpace: 'nowrap',
                                  color: FP.color.danger,
                                },
                              }}
                              inputProps={{
                                min: 0,
                                max: tope,
                                step: '0.01',
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: 140, ...fpInputSx }}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontSize: '0.82rem', color: FP.color.textSecondary }}
                          >
                            {f.cant_notas_credito > 0
                              ? `${f.cant_notas_credito} ($${fmtMoney(f.total_notas_credito)})`
                              : '—'}
                          </TableCell>
                          <TableCell align="center" className="no-print">
                            <Tooltip title="Reimprimir factura">
                              <IconButton
                                size="small"
                                onClick={() => reimprimirFactura(f.nfact, f.tipofactura)}
                                sx={{
                                  color: FP.color.accentBlue,
                                  '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                }}
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <VistaFactura
        open={vistaFacturaAbierta}
        onClose={() => setVistaFacturaAbierta(false)}
        facturaData={datosVistaFactura}
      />
    </Box>
  );
};

export default RegistroPagosClientesCredito;
